import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { useAuthContext } from './AuthProvider';
import LoadingFullscreen from 'components/common/loading/LoadingFullscreen';

const authRequestInterceptor = async (
  config: InternalAxiosRequestConfig,
  clientToken?: string,
): Promise<InternalAxiosRequestConfig> => {
  const { headers } = config;
  if (clientToken) {
    headers['Authorization'] = `Client ${clientToken}`;
  }
  return config;
};

const notAuthenticatedErrorInterceptor = async (
  error: AxiosError,
  refetch: (() => any) | undefined,
) => {
  const config = error.config;
  if (error.response?.status === 401 && refetch) {
    // refresh client token
    const { data } = await refetch();

    return axios({
      url: config?.url,
      method: config?.method,
      headers: {
        ...config?.headers,
        Authorization: `Client ${data.access_token}`,
      },
    });
  }
  return Promise.reject(error);
};

const customAxios = axios.create({
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const AxiosContext = React.createContext<AxiosInstance>(customAxios);

export function AxiosContextProvider(props: PropsWithChildren) {
  const [isReady, setIsReady] = useState(false);
  const { clientToken, refetch } = useAuthContext();

  useEffect(() => {
    const requestInterceptors = customAxios.interceptors.request.use(async (config) =>
      authRequestInterceptor(config, clientToken),
    );

    const responseInterceptors = customAxios.interceptors.response.use(
      (response) => response,
      async (error) => notAuthenticatedErrorInterceptor(error, refetch),
    );

    setIsReady(true);

    return () => {
      customAxios.interceptors.request.eject(requestInterceptors);
      customAxios.interceptors.response.eject(responseInterceptors);
    };
  }, [clientToken, refetch]);

  return (
    <AxiosContext.Provider value={customAxios}>
      {!isReady && (
        <div className="h-screen" data-testid="provider-axios-loading">
          <LoadingFullscreen />
        </div>
      )}
      {isReady && <>{props.children}</>}
    </AxiosContext.Provider>
  );
}

export const useAxios = () => {
  const context = useContext(AxiosContext);
  if (!context) {
    throw new Error('useAxios hook must be used within an AxiosContextProvider');
  }

  return context;
};
